// appearance.ts — the console HOME SCREEN's half of the per-user appearance
// seat (console ui_state kind 'ui_prefs', key 'global' — migration 027).
//
// Before this, the home screen had no theme machinery at all: styles.css was a
// single hardcoded dark :root and app.ts's comment said so outright ("Until
// the console grows appearance prefs, the defaults ARE the prefs"). So a user
// who picked Solarized Light inside a release still got a dark console home.
// The owner's requirement is one appearance per USER across every surface, and
// the home screen is one of the three.
//
// The mechanism deliberately mirrors the Bridge's (helm-bridge/src/client/
// appearance.ts), because they must agree pixel-for-pixel on the same theme:
//
//   Layer 1 — CSS. styles.css carries a full DARK token set on bare `:root`
//     and a LIGHT set under `:root[data-theme="light"]`. Mode alone therefore
//     renders correctly with zero JS.
//   Layer 2 — JS. The active theme's 16 ui tokens are written as INLINE custom
//     properties on <html>, which beat both blocks. Selecting a different
//     theme re-writes them; every token the theme does not set is removed, so
//     switching back to a built-in fully clears the previous theme.
//
// Precedence at boot is the same one-directional rule as the Bridge:
//   1. index.html's pre-paint snippet replays localStorage — no network, so
//      no flash. Possibly stale.
//   2. hydrate() — the console seat is the TRUTH and overwrites the cache.
//
// READ-WRITE as of the home-screen overhaul. This module used to say
// "READ-ONLY, deliberately": the home screen honoured the preference but had no
// control to change it, and the named blocker was AG Grid, which takes its
// palette as CREATION-TIME params (gridThemeParams()) rather than CSS tokens.
// The first fix REMOUNTED the grid on every theme change; the foundation seam
// retired that: gridThemeParams() now hands AG Grid `var(--hc-*)` STRINGS (the
// Helm's glance-grid shape), so the palette resolves at PAINT time and a theme
// flip re-skins the grid like any other element — no destroy, no rebuild.
// saveSeat() below is the write half; theme-control.ts is the control.

import {
  resolveTheme,
  THEME_VAR_NAMES,
  UI_TOKEN_VARS,
  type AppTheme,
} from './generated/themes.js';
import {
  DEFAULT_STYLE_ID, normalizeStyleId, offeredStyles, resolveStyleSeat,
  STYLE_SEAT_PARAM, STYLE_SEAT_SESSION_KEY, type AppStyle,
} from './generated/styles.js';
// The style GROUND (owner correction 2026-09-07) — see apply() step 1c.
import { groundStyle } from './style-substrate.js';

/** Mode + both theme slots — byte-identical to the Bridge's AppearanceSeat.
 * The two clients read and write the SAME row, so this shape is a contract,
 * not a local convenience. */
export interface AppearanceSeat {
  theme: 'dark' | 'light';
  themeDark: string;
  themeLight: string;
  /** STYLE axis (helm-bridge/src/client/styles.ts). One global pick; both
   * theme slots live inside it. OPTIONAL on the wire: every row written
   * before PR-4 lacks the key, and a required field would make parseSeat()
   * reject them all. */
  style?: string;
}

/** The apex path. The Bridge reaches the identical row through the proxy's
 * /__console/* carve-out; the home screen is already on the apex, so no
 * prefix. Both resolve to `ui_state(kind='ui_prefs', key='global')`. */
export const SEAT_URL = '/api/ui-state/ui-prefs/global';

/** Defaults match the Bridge's (store.ts) so a user who has never set a theme
 * sees the same thing on both surfaces. */
export const DEFAULT_SEAT: AppearanceSeat = {
  theme: 'dark',
  themeDark: 'mission-control',
  themeLight: 'chart-table',
  style: DEFAULT_STYLE_ID,
};

const LS_MODE = 'hc-theme';
const LS_VARS = 'hc-theme-vars';
// The style pre-paint mirror (index.html replays it), the twin of LS_MODE.
// Owner ruling 2026-09-06 (supersedes #490 Blueprint-only): the home renders
// the chosen style too, so it stamps `data-style` and pre-paints it exactly as
// it does the mode. The value written here is the PAINTED (seat-gated) style —
// see paintedStyleId() — so the pre-paint replay never carries a style the
// dark-launch seat does not offer.
const LS_STYLE = 'hc-style';
/** Same-origin realtime theme signal, shared with the help portal (see the
 * comment in apply()). Exported for the probes. */
export const APPEARANCE_BUMP_KEY = 'appearance-seat-rev';

// ---------------------------------------------------------------------
// THE STYLE-AXIS SEAT, mirroring the Helm's style-axis.ts.
// ---------------------------------------------------------------------
//
// The seat is LIVE by default: STYLE_PICKER_SEAT_DEFAULT is true in the shared
// registry (styles.ts) since #493, so the picker offers all eight styles and
// the paint follows the stored pick. The GATE stays even so: the paint is
// tied to what the seat OFFERS, never to the stored pick alone, so a seat
// switched back off (the `?style-picker=off` session lane, sticky per tab, or
// a future default flip) can never strand someone on a style they can no
// longer change. The console has no Bridge `prefs.stylePicker`, so it
// resolves the seat from that SESSION-OVERRIDE lane plus the shared default —
// the exact owner-checkpoint mechanism the Helm uses to review a style live
// without a deploy — and the one shared registry constant decides the Bridge
// home and the Helm together.

/** `?style-picker=on|off` -> sticky sessionStorage; undefined otherwise. The
 * console twin of style-axis.ts sessionStyleSeatOverride(). */
function sessionStyleSeatOverride(): boolean | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const q = new URLSearchParams(window.location.search).get(STYLE_SEAT_PARAM);
    if (q === 'on' || q === '1') { sessionStorage.setItem(STYLE_SEAT_SESSION_KEY, 'on'); return true; }
    if (q === 'off' || q === '0') { sessionStorage.setItem(STYLE_SEAT_SESSION_KEY, 'off'); return false; }
    const stored = sessionStorage.getItem(STYLE_SEAT_SESSION_KEY);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
  } catch {
    /* private windows — the default decides */
  }
  return undefined;
}

/** Is the style picker live on this surface right now? */
export function styleSeatEnabled(): boolean {
  return resolveStyleSeat({ sessionOverride: sessionStyleSeatOverride(), serverPref: undefined }).enabled;
}

/** The styles a picker may offer right now (Blueprint alone while the seat is
 * off). The one choke point — the control asks this, never the registry. */
export function pickableStyles(): AppStyle[] {
  return offeredStyles(styleSeatEnabled());
}

/** The style actually PAINTED for a seat: the stored pick when the seat offers
 * it, Blueprint otherwise. Mirrors style-axis.ts paintStyle()'s gate. */
export function paintedStyleId(seat: AppearanceSeat): string {
  const wanted = normalizeStyleId(seat.style);
  const offered = new Set(pickableStyles().map((s) => s.id));
  return offered.has(wanted) ? wanted : DEFAULT_STYLE_ID;
}

/** Set the style pick, leaving the theme slots alone — the picker's payload. */
export function withStyle(seat: AppearanceSeat, styleId: string): AppearanceSeat {
  return { ...seat, style: normalizeStyleId(styleId) };
}

/** Narrow an unknown row; a partial blob yields null rather than a
 * half-applied theme. Mirrors the Bridge's parseSeat. */
export function parseSeat(raw: unknown): AppearanceSeat | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const mode = o.theme === 'light' ? 'light' : o.theme === 'dark' ? 'dark' : null;
  if (!mode) return null;
  if (typeof o.themeDark !== 'string' || typeof o.themeLight !== 'string') return null;
  return {
    theme: mode,
    themeDark: o.themeDark,
    themeLight: o.themeLight,
    // Absent on every pre-PR-4 row; normalized so an unknown id paints the
    // identity style rather than nothing.
    style: normalizeStyleId(typeof o.style === 'string' ? o.style : undefined),
  };
}

/** The theme a seat resolves to for its own mode AND ITS PAINTED STYLE.
 *
 * Unknown or cross-mode slot ids fall back to that mode's built-in default
 * (resolveTheme's contract), so a theme deleted from the registry degrades
 * instead of breaking the page.
 *
 * THE THIRD ARGUMENT IS THE OWNER'S "COLORS" GAP (correction 2026-09-07).
 * `resolveTheme(id, mode, styleId)` defaults styleId to the IDENTITY style, and
 * this call omitted it — so a stored `mission-control` stayed `mission-control`
 * under every style, and normalizeThemeId's whole style-restriction ("a cross-
 * style id normalizes to the painted style's own default") never fired here.
 * The Helm passes `currentStyle()` / `style.id` at both of its call sites.
 * Measured in a real browser before this line changed, style=mainframe,
 * dark mode, same seat: the Helm resolved `--mat-shell-bg` to #030A05 (its
 * curated GREEN PHOSPHOR theme) while the Bridge home resolved #060910 —
 * Blueprint's palette under Mainframe's materials. Fonts, sprite and ground
 * had all converged by then; the palette was the last axis still forked, and
 * it is the one the owner named third. Blueprint is unaffected: the painted id
 * IS the identity id there, which is exactly the default this replaces. */
export function themeFor(seat: AppearanceSeat): AppTheme {
  return resolveTheme(
    seat.theme === 'light' ? seat.themeLight : seat.themeDark,
    seat.theme,
    paintedStyleId(seat),
  );
}

/** The CSS custom properties for a theme's 16 ui tokens.
 *
 * NOTE this is deliberately NOT the Bridge's themeCssVars(): that returns {}
 * for the two built-ins because on the Bridge the built-ins ARE styles.css.
 * The console's stylesheet is its own (--hc-* names), so the home screen must
 * write the tokens for EVERY theme, built-in included, or picking Mission
 * Control would leave the console on whatever the previous theme set. */
export function themeTokenVars(theme: AppTheme): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, cssVar] of Object.entries(UI_TOKEN_VARS)) {
    const v = (theme.ui as unknown as Record<string, string>)[key];
    if (typeof v === 'string' && v) out[cssVar] = v;
  }
  // Map the Bridge's token vocabulary onto the console's own --hc-* names, so
  // the existing stylesheet follows the theme without a full rewrite.
  out['--hc-bg'] = theme.ui.bg;
  out['--hc-fg'] = theme.ui.ink;
  out['--hc-accent'] = theme.ui.accent;
  out['--hc-muted'] = theme.ui.ink3;
  out['--hc-line'] = theme.ui.border;
  out['--hc-surface'] = theme.ui.surface;
  out['--hc-surface2'] = theme.ui.surface2;
  // Theme-truth (owner ruling 2026-09-05, the Helm is the source of truth):
  // the Helm's ramp orders bg-deep < bg < surface < surface-2 — raised
  // chrome sits ABOVE the ground — so the panel seat (dialogs, menus,
  // cards) rides `surface`, and `bgDeep` feeds the SHELL chrome seat
  // (--hc-bg-deep: header strip + pagebar, grid row striping), exactly how
  // the Helm spends it (.tabstrip/.rail/.trailbar ride --bg-deep).
  out['--hc-panel'] = theme.ui.surface;
  out['--hc-bg-deep'] = theme.ui.bgDeep;
  out['--hc-track'] = theme.ui.surface3;

  // ---- theme-reach widening (design-system-foundation) ----
  // Before this, only the nine names above followed the theme: every scrim,
  // danger colour, raised border and secondary ink was MODE-only, so dialogs
  // were literally not themed. Four of the widened names have DIRECT seats in
  // UiTokens — the theme's own value is the honest source:
  out['--hc-ink2'] = theme.ui.ink2;
  out['--hc-border2'] = theme.ui.border2;
  out['--hc-accent-dim'] = theme.ui.accentDim;
  out['--hc-onaccent'] = theme.ui.onaccent;
  // The rest have NO UiTokens seat (widening UiTokens is the named five-file
  // deferral on shared-shell's R72 ledger), so they are color-mix DERIVATIONS
  // over tokens that DO follow the theme — emitted as CSS expressions that
  // resolve at paint, referencing the inline vars written above.
  //   danger: semantic red hue (the mode's red-pill fg — the 18 pill colours
  //   are the other named deferral) pulled toward the theme's ink, so danger
  //   tracks the theme's brightness without losing its meaning.
  out['--hc-danger'] = 'color-mix(in srgb, var(--hc-p-red-fg) 72%, var(--hc-fg))';
  //   scrim/shadow: near-black tinted by the theme's ground, alpha per mode
  //   (matching the styles.css floors' measured alphas: scrim .55/.38,
  //   shadow .45/.18).
  const tint = 'color-mix(in srgb, var(--hc-bg) 22%, black)';
  out['--hc-scrim'] =
    theme.mode === 'light'
      ? `color-mix(in srgb, ${tint} 38%, transparent)`
      : `color-mix(in srgb, ${tint} 55%, transparent)`;
  out['--hc-shadow'] =
    theme.mode === 'light'
      ? `0 16px 40px color-mix(in srgb, ${tint} 18%, transparent)`
      : `0 16px 40px color-mix(in srgb, ${tint} 45%, transparent)`;
  //   glow (R36): shadow GEOMETRY, consumed as `box-shadow: var(--hc-glow)
  //   <color>`. OFF in light mode — the Helm's own --glow is deliberately
  //   `0 0 0` in light (owner ruling Q4's dispatched default).
  out['--hc-glow'] = theme.mode === 'light' ? '0 0 0' : '0 0 8px';

  // ---- chat palette (theme-truth) ----
  // The assistant pane is a CHAT surface: its --as-* seat (styles.css
  // .as-root) prefers the theme's CHAT palette over the UI tokens. Written
  // in pi-theme.ts's --pi-* vocabulary so both surfaces' chat lanes speak
  // one language; every derived fill is a CSS color-mix over --pi-fg/--pi-bg
  // so contrast tracks whichever theme is active (the Helm's chat doctrine).
  out['--pi-bg'] = theme.chat.background;
  out['--pi-fg'] = theme.chat.foreground;
  out['--pi-cursor'] = theme.chat.cursor;
  out['--pi-accent'] = theme.chat.blue;
  out['--pi-accent-2'] = theme.chat.cyan;
  out['--pi-err'] = theme.chat.red;
  out['--pi-warn'] = theme.chat.yellow;
  out['--pi-ok'] = theme.chat.green;
  // The send accent: themes write --send, never --pi-send (themes.ts NOTE);
  // a theme without one falls to the terracotta floor in styles.css :root.
  if (theme.send) out['--send'] = theme.send;
  return out;
}

/** Every var name apply() may set — so switching themes can REMOVE the ones
 * the new theme does not define instead of leaving the old value behind.
 *
 * INVARIANT (tested in home-chrome.test.mjs): every name themeTokenVars()
 * can emit appears here. A var missing from this list is never REMOVED on a
 * theme switch — the old theme's colour persists and reads as a rendering
 * glitch. Exported so the test can hold the two lists together. */
export const MANAGED_VARS: readonly string[] = [
  ...THEME_VAR_NAMES,
  '--hc-bg',
  '--hc-fg',
  '--hc-accent',
  '--hc-muted',
  '--hc-line',
  '--hc-surface',
  '--hc-surface2',
  '--hc-panel',
  '--hc-track',
  // theme-reach widening (design-system-foundation) — one entry per name
  // themeTokenVars() gained, same order:
  '--hc-ink2',
  '--hc-border2',
  '--hc-accent-dim',
  '--hc-onaccent',
  '--hc-danger',
  '--hc-scrim',
  '--hc-shadow',
  '--hc-glow',
  // theme-truth (shell chrome + chat palette) — one entry per name
  // themeTokenVars() gained, same order:
  '--hc-bg-deep',
  '--pi-bg',
  '--pi-fg',
  '--pi-cursor',
  '--pi-accent',
  '--pi-accent-2',
  '--pi-err',
  '--pi-warn',
  '--pi-ok',
];

/** AG Grid's theming API takes its own param names — but it accepts `var()`
 * STRINGS (the Helm's glance-grid proves the shape), so the params are the
 * console's own tokens and the palette resolves at PAINT time. That is what
 * retired home.ts's destroy-and-rebuild-on-theme-change: a theme flip
 * rewrites the inline custom properties and the grid repaints like any other
 * element. One derivation, used by every console grid (home via buildGrid,
 * users/audit via admin-ui's makeGrid).
 *
 * The six font params + cell padding mirror the Helm's bridgeTheme
 * (glance-grid.ts) BY VALUE: WaaG's fonts on headers and cells (R34) instead
 * of AG Grid quartz's silently-inherited IBM Plex Sans. Header CASING is CSS
 * (.hc-grid .ag-header-cell-text), not data — no column renaming.
 *
 * The row-metric decision this note used to hand to users-audit-parity
 * (R59c) is TAKEN: makeGrid's rowHeight:44/headerHeight:38 overrides are
 * deleted, so the admin grids derive their metrics from these params the
 * same way the release grid does — three grids, one derivation. */
export function gridThemeParams(): Record<string, string> {
  // Theme-truth (owner ruling 2026-09-05): the param set mirrors the Helm's
  // bridgeTheme (glance-grid.ts) ENTRY BY ENTRY, translated 1:1 into the
  // console's token vocabulary (--surface→--hc-surface, --surface-2→
  // --hc-surface2, --surface-3→--hc-track, --ink-2→--hc-ink2, --bg-deep→
  // --hc-bg-deep, --accent-dim→--hc-accent-dim, --radius→--hc-radius).
  // The grid body sits one step ABOVE the app ground (surface, never bg),
  // headers ride surface-2 with ink-2, odd rows stripe on bg-deep, hover is
  // the surface-3 step, and density is the Helm's 30px rows.
  return {
    backgroundColor: 'var(--hc-surface)',
    foregroundColor: 'var(--hc-fg)',
    borderColor: 'var(--hc-line)',
    chromeBackgroundColor: 'var(--hc-surface2)',
    headerBackgroundColor: 'var(--hc-surface2)',
    headerTextColor: 'var(--hc-ink2)',
    oddRowBackgroundColor: 'var(--hc-bg-deep, var(--hc-surface2))',
    rowHoverColor: 'var(--hc-track)',
    selectedRowBackgroundColor: 'var(--hc-accent-dim)',
    accentColor: 'var(--hc-accent)',
    inputBorder: 'var(--hc-border2)',
    inputBackgroundColor: 'var(--hc-surface)',
    menuBackgroundColor: 'var(--hc-surface2)',
    tooltipBackgroundColor: 'var(--hc-surface2)',
    tooltipTextColor: 'var(--hc-fg)',
    // Faces ride the role tokens (PR-1). The two body rows park on
    // --font-ui with a deferred re-role: a data grid rendering the UI face
    // is the audit's finding, and changing it here would not be identity.
    fontFamily: 'var(--font-ui)', // deferred re-role -> --font-data
    cellFontFamily: 'var(--font-ui)', // deferred re-role -> --font-data
    headerFontFamily: 'var(--font-accent)',
    fontSize: '12px',
    headerFontSize: '10px',
    headerFontWeight: '700',
    borderRadius: '6px',
    wrapperBorderRadius: 'var(--hc-radius)',
    rowHeight: '30px',
    headerHeight: '30px',
    cellHorizontalPadding: '10px',
  };
}

/**
 * Apply a seat change LOCALLY and persist it to the per-user seat.
 *
 * Optimistic on purpose: apply() runs first so the page re-skins on the click
 * rather than after a network round-trip, and the PUT follows. A failed write
 * is REPORTED, not swallowed — a theme that silently reverts on the next load
 * is the exact complaint this seat was built to answer, so the caller gets a
 * false and can say so.
 *
 * Unconditional {state} form (no rev): appearance is last-write-wins by
 * design. Two browsers racing over one user's own theme is not a conflict
 * worth a 409 dance — the last click the human made should win.
 */
export async function saveSeat(
  next: AppearanceSeat,
  fetchFn: typeof fetch = fetch,
): Promise<boolean> {
  apply(next);
  // Our own write moves the row, so the cached tag is stale — the next poll
  // must be a full read rather than a 304 against a version that is gone.
  seatEtag = null;
  try {
    const res = await fetchFn(SEAT_URL, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ state: next }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Flip the mode, keeping both theme slots. The toggle's whole payload. */
export function toggledMode(seat: AppearanceSeat): AppearanceSeat {
  return { ...seat, theme: seat.theme === 'light' ? 'dark' : 'light' };
}

/** Set the theme id for ONE mode slot, leaving the other alone — the picker
 * edits the slot for the mode currently rendering. */
export function withThemeForMode(
  seat: AppearanceSeat,
  mode: 'dark' | 'light',
  themeId: string,
): AppearanceSeat {
  return mode === 'light' ? { ...seat, themeLight: themeId } : { ...seat, themeDark: themeId };
}

/** Listeners re-read derived palettes (AG Grid, pi views) after a change. */
const listeners = new Set<(seat: AppearanceSeat) => void>();
export function onAppearanceChange(fn: (seat: AppearanceSeat) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

let current: AppearanceSeat = { ...DEFAULT_SEAT };

/** The seat as last applied — read by the AG Grid + pi-view palette seams. */
export function currentSeat(): AppearanceSeat {
  return current;
}

/** The theme as last applied. */
export function currentTheme(): AppTheme {
  return themeFor(current);
}

/** The user's stored style pick (normalized) — what the picker shows selected.
 * Distinct from the PAINTED style (paintedStyleId), which the seat may gate to
 * Blueprint while leaving the stored pick intact. */
export function currentStyleId(): string {
  return normalizeStyleId(current.style);
}

/** THE apply path: stamp the mode signal, write the theme's tokens inline,
 * refresh the pre-paint cache, and notify the derived-palette consumers. */
export function apply(seat: AppearanceSeat): void {
  current = seat;
  const root = document.documentElement;

  // 1. Mode signal — dark is the default (no attribute), same as the Bridge.
  if (seat.theme === 'light') root.setAttribute('data-theme', 'light');
  else root.removeAttribute('data-theme');
  // 1b. STYLE signal — STAMPED (owner ruling 2026-09-06, supersedes the WF-C
  //     Blueprint-only ruling #490). The chosen UI STYLE is unified across ALL
  //     surfaces; the home renders it too. style-materials.css (generated from
  //     the Helm's styles.css) now carries the eight styles' `[data-style]`
  //     material tables, and styles.css wires the console chrome onto their
  //     --mat-*/--bgl-* tokens — so the stamp selects a real material, not the
  //     nothing #490 removed. The stamp is SEAT-GATED (paintedStyleId): while
  //     the dark-launch seat is off the paint is Blueprint, so nobody is
  //     stranded on a style they cannot re-pick — the exact rule style-axis.ts
  //     paintStyle() enforces on the Helm.
  root.setAttribute('data-style', paintedStyleId(seat));
  // 1c. The style's GROUND (owner correction 2026-09-07). The material tables
  //     carry the `#style-bg` rules; something has to insert the ELEMENT they
  //     select, or the five canvas-ground styles and the one texture-ground
  //     style render their CSS-only still here while the Helm animates. This
  //     is the console's twin of the Helm's onStylePainted() and rides the
  //     SAME apply path as the stamp, so a style switch moves the ground with
  //     the material rather than one repaint later. It also arms the GLASS
  //     specular tracker and syncs STARSHIP's mode-dependent field.
  //     applyStyleBackground() applies the viewer-preference gates itself
  //     (prefers-reduced-motion / -transparency and the app's own
  //     reduce-effects seat), so a degraded grade is a return value, not a
  //     branch here.
  groundStyle(paintedStyleId(seat));

  // 2. Theme tokens as inline custom properties (they beat both CSS blocks).
  const theme = themeFor(seat);
  const vars = themeTokenVars(theme);
  for (const name of MANAGED_VARS) {
    const v = vars[name];
    if (v !== undefined) root.style.setProperty(name, v);
    else root.style.removeProperty(name);
  }

  // 3. Pre-paint cache for the next load (index.html replays it) + the
  // same-origin realtime bump: the help portal (served at this origin at
  // /help/) listens for a 'storage' event on APPEARANCE_BUMP_KEY and
  // re-reads the seat immediately — and this tab listens for the portal's
  // bump the same way (startAppearanceSync). Cross-origin surfaces (the
  // Helm on release subdomains) still ride the 12 s poll; storage events
  // cannot cross origins. The key name is a 2-line contract with
  // helm-bridge/src/client/portal/theme.ts — change them TOGETHER.
  try {
    localStorage.setItem(LS_MODE, seat.theme);
    // The style pre-paint mirror (index.html replays it) — the PAINTED, seat-
    // gated style, so a hard refresh's first paint carries exactly what the
    // running app does and never a style the seat does not offer.
    localStorage.setItem(LS_STYLE, paintedStyleId(seat));
    localStorage.setItem(LS_VARS, JSON.stringify(vars));
    localStorage.setItem(APPEARANCE_BUMP_KEY, String(Date.now()));
  } catch {
    // storage unavailable (private mode) — pre-paint just defaults to dark
  }

  // 4. Browser chrome follows the theme ground.
  for (const m of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    m.content = theme.ui.bg;
  }

  for (const fn of listeners) {
    try {
      fn(seat);
    } catch {
      /* a bad listener must not stop the re-skin */
    }
  }
}

/**
 * Load the per-user seat and apply it. Returns the seat actually applied.
 *
 * A 404 means "no row yet" (the user has never chosen a theme anywhere) — the
 * defaults apply, and the first change made in any release creates the row.
 * A 401/network failure leaves whatever the pre-paint snippet painted, which
 * is the last thing this browser saw.
 */
export async function hydrate(
  fetchFn: typeof fetch = fetch,
  timeoutMs = HYDRATE_TIMEOUT_MS,
): Promise<AppearanceSeat> {
  let seat: AppearanceSeat | null = null;
  try {
    // BOUNDED, and that bound is load-bearing: app.ts awaits this before the
    // first mount (AG Grid needs the palette at creation time), and `fetch`
    // has NO default timeout. A hung request would otherwise mean a
    // permanently blank home screen — trading "wrong colors for a moment" for
    // "no page at all" is never the right trade. On timeout we paint from the
    // cache, exactly as if the user were offline.
    const res = await withTimeout(fetchFn, SEAT_URL, timeoutMs);
    if (res && res.status === 200) {
      const body = (await res.json()) as { state?: unknown };
      seat = parseSeat(body.state);
    }
  } catch {
    /* offline, aborted, or unparseable — fall through to the cached paint */
  }
  const applied = seat ?? cachedOrDefault();
  apply(applied);
  return applied;
}

/** How often the home screen re-reads the seat.
 *
 * Same reasoning and the same number as the Bridge client's
 * APPEARANCE_POLL_MS: appearance is one choice per user across every surface,
 * the home screen is one of those surfaces, and a theme changed inside a
 * release must land here too — without a reload. The read is a conditional GET
 * that answers 304 when nothing moved.
 */
export const APPEARANCE_POLL_MS = 12_000;

/** Last ETag seen, replayed as If-None-Match so a poll costs a 304. */
let seatEtag: string | null = null;

let pollTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Subscribe the home screen to the seat.
 *
 * Visibility-aware for the same reason as the Bridge's: a hidden tab has
 * nothing to repaint, and the moment that matters is when someone comes back
 * to it — so focus/visibilitychange forces an immediate read rather than
 * waiting out the interval.
 *
 * apply() rewrites the inline theme tokens (and notifies onAppearanceChange
 * listeners for value-consumers like the pi-view palette) — so a theme
 * changed in another tab re-skins the whole page here, AG Grid included via
 * its var() params, not just the chrome.
 */
export function startAppearanceSync(intervalMs: number = APPEARANCE_POLL_MS): () => void {
  stopAppearanceSync();

  const tick = async (): Promise<void> => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    try {
      const res = await withTimeout(fetch, SEAT_URL, HYDRATE_TIMEOUT_MS, seatEtag);
      if (!res) return;
      if (res.status === 304) return; // nothing moved — the common answer
      const tag = res.headers?.get?.('etag');
      if (tag) seatEtag = tag;
      if (res.status !== 200) return;
      const body = (await res.json()) as { state?: unknown };
      const next = parseSeat(body.state);
      if (!next) return;
      const cur = currentSeat();
      if (next.theme === cur.theme && next.themeDark === cur.themeDark
        && next.themeLight === cur.themeLight
        && normalizeStyleId(next.style) === normalizeStyleId(cur.style)) return; // already in sync
      apply(next);
    } catch {
      /* a poll must never throw into a timer */
    }
  };

  pollTimer = setInterval(() => void tick(), intervalMs);
  const onWake = (): void => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') void tick();
  };
  const onStorage = (e: StorageEvent): void => {
    // The help portal's (or another console tab's) theme commit — re-read
    // the seat NOW instead of waiting out the poll window.
    if (e.key === APPEARANCE_BUMP_KEY) void tick();
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onWake);
  if (typeof window !== 'undefined') window.addEventListener('focus', onWake);
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);

  return () => {
    stopAppearanceSync();
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onWake);
    if (typeof window !== 'undefined') window.removeEventListener('focus', onWake);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

export function stopAppearanceSync(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/** Reported by the harness so an unstarted poller cannot pass as a running one. */
export function appearanceSyncArmed(): boolean {
  return pollTimer !== null;
}

/** How long the first mount will wait on the seat before painting from cache. */
export const HYDRATE_TIMEOUT_MS = 2000;

/** GET with an abort-backed deadline. Returns null when the deadline wins. */
async function withTimeout(
  fetchFn: typeof fetch,
  url: string,
  ms: number,
  etag?: string | null,
): Promise<Response | null> {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      try {
        ctl?.abort();
      } catch {
        /* aborting is best-effort; the race is already decided */
      }
      resolve(null);
    }, ms);
  });
  try {
    const init: RequestInit = {};
    if (ctl) init.signal = ctl.signal;
    if (etag) init.headers = { 'if-none-match': etag };
    return await Promise.race([fetchFn(url, init), deadline]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** What to paint when the server has nothing to say: the cached MODE (the
 * theme ids are not cached — only the resolved vars are, and those are
 * already on the page from the pre-paint snippet). */
function cachedOrDefault(): AppearanceSeat {
  try {
    const mode = localStorage.getItem(LS_MODE);
    if (mode === 'light' || mode === 'dark') return { ...DEFAULT_SEAT, theme: mode };
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_SEAT };
}
